import { useEffect, useMemo, useState } from "react";
import api from "../services/api";
import ViewerSidebar from "../components/ViewerSidebar";
import { downloadEsmpFile } from "../utils/fileDownload";

const getStatusClasses = (status) => {
  switch (status) {
    case "Approved":
      return "bg-green-100 text-green-700";
    case "Returned":
    case "Rejected":
      return "bg-red-100 text-red-700";
    case "Overdue":
      return "bg-orange-100 text-orange-700";
    case "Pending":
    case "Submitted":
      return "bg-yellow-100 text-yellow-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const getStageText = (item) => {
  if (["Approved", "Returned", "Rejected"].includes(item.status)) {
    return "Review completed";
  }

  if (item.reviewer_id && ["Pending", "Submitted", "Overdue"].includes(item.status)) {
    return "Assigned to reviewer";
  }

  return "Submitted and awaiting assignment";
};

export default function ViewerDashboard() {
  const [search, setSearch] = useState("");
  const viewerPrefs = (() => {
    try {
      return JSON.parse(localStorage.getItem("settings:viewer")) || {};
    } catch {
      return {};
    }
  })();
  const [statusFilter, setStatusFilter] = useState(viewerPrefs.defaultStatusFilter || "all");
  const [esmps, setEsmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const compactTables = !!viewerPrefs.compactTables;

  const fetchEsmps = async () => {
    try {
      setLoading(true);
      const res = await api.get("/esmp/list");
      const payload = res.data?.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.esmps)
          ? payload.esmps
          : [];

      const sorted = [...list].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setEsmps(sorted);
    } catch (error) {
      console.error("Failed to fetch ESMP list:", error);
      setEsmps([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEsmps();
  }, []);

  const filtered = useMemo(() => {
    return esmps.filter((item) => {
      const matchesSearch =
        (item.esmp_id || "").toLowerCase().includes(search.toLowerCase()) ||
        (item.project_name || "").toLowerCase().includes(search.toLowerCase()) ||
        (item.district || "").toLowerCase().includes(search.toLowerCase());

      const matchesStatus = statusFilter === "all" || item.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [esmps, search, statusFilter]);

  return (
    <div className="flex min-h-screen bg-gray-100">
      <ViewerSidebar />

      <main className="flex-1 p-6 space-y-6">
        <div className="bg-gradient-to-r from-green-700 to-green-500 text-white p-5 rounded-xl shadow-md">
          <h2 className="text-2xl font-bold">ESMP Tracking System</h2>
          <p className="text-sm mt-1">View all ESMP documents and current processing stage</p>
        </div>

        <div className="bg-white p-4 rounded-xl shadow grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by ESMP ID, project, district"
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-600 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-600 outline-none"
            >
              <option value="all">All statuses</option>
              <option value="Submitted">Submitted</option>
              <option value="Pending">Pending</option>
              <option value="Overdue">Overdue</option>
              <option value="Approved">Approved</option>
              <option value="Returned">Returned</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchEsmps}
              className="w-full px-4 py-2 bg-green-700 text-white rounded-lg font-semibold hover:bg-green-800"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl shadow border-l-4 border-green-600">
            <p className="text-xs text-gray-500">Total Documents</p>
            <h3 className="text-2xl font-bold text-green-700">{esmps.length}</h3>
          </div>
          <div className="bg-white p-4 rounded-xl shadow border-l-4 border-yellow-500">
            <p className="text-xs text-gray-500">Pending / Submitted</p>
            <h3 className="text-2xl font-bold text-yellow-700">
              {esmps.filter((e) => ["Pending", "Submitted"].includes(e.status)).length}
            </h3>
          </div>
          <div className="bg-white p-4 rounded-xl shadow border-l-4 border-red-500">
            <p className="text-xs text-gray-500">Overdue</p>
            <h3 className="text-2xl font-bold text-red-700">{esmps.filter((e) => e.status === "Overdue").length}</h3>
          </div>
          <div className="bg-white p-4 rounded-xl shadow border-l-4 border-blue-500">
            <p className="text-xs text-gray-500">Reviewed</p>
            <h3 className="text-2xl font-bold text-blue-700">
              {esmps.filter((e) => ["Approved", "Returned", "Rejected"].includes(e.status)).length}
            </h3>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading documents...</p>
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-500 py-6">No documents found for the selected filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className={`border text-left ${compactTables ? "px-2 py-1" : "px-3 py-2"}`}>ESMP ID</th>
                    <th className={`border text-left ${compactTables ? "px-2 py-1" : "px-3 py-2"}`}>Project</th>
                    <th className={`border text-left ${compactTables ? "px-2 py-1" : "px-3 py-2"}`}>District</th>
                    <th className={`border text-left ${compactTables ? "px-2 py-1" : "px-3 py-2"}`}>Submitted Date</th>
                    <th className={`border text-left ${compactTables ? "px-2 py-1" : "px-3 py-2"}`}>Risk Rating</th>
                    <th className={`border text-left ${compactTables ? "px-2 py-1" : "px-3 py-2"}`}>Document</th>
                    <th className={`border text-left ${compactTables ? "px-2 py-1" : "px-3 py-2"}`}>Status</th>
                    <th className={`border text-left ${compactTables ? "px-2 py-1" : "px-3 py-2"}`}>Stage</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => (
                    <tr key={item.id} className="hover:bg-green-50">
                      <td className={`border font-medium text-blue-700 ${compactTables ? "px-2 py-1" : "px-3 py-2"}`}>
                        {item.esmp_id || "N/A"}
                      </td>
                      <td className={`border ${compactTables ? "px-2 py-1" : "px-3 py-2"}`}>
                        {item.project_name || "N/A"}
                      </td>
                      <td className={`border ${compactTables ? "px-2 py-1" : "px-3 py-2"}`}>
                        {item.district || "N/A"}
                      </td>
                      <td className={`border text-gray-600 ${compactTables ? "px-2 py-1" : "px-3 py-2"}`}>
                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "N/A"}
                      </td>
                      <td className={`border ${compactTables ? "px-2 py-1" : "px-3 py-2"}`}>
                        {item.risk_rating || item.riskRating || "N/A"}
                      </td>
                      <td className={`border ${compactTables ? "px-2 py-1" : "px-3 py-2"}`}>
                        {item.file_path ? (
                          <button
                            type="button"
                            onClick={() => downloadEsmpFile(item)}
                            className="text-blue-600 hover:underline"
                          >
                            {item.file_name || "Open document"}
                          </button>
                        ) : (
                          <span className="text-gray-500">No file</span>
                        )}
                      </td>
                      <td className={`border ${compactTables ? "px-2 py-1" : "px-3 py-2"}`}>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusClasses(item.status)}`}>
                          {item.status || "Unknown"}
                        </span>
                      </td>
                      <td className={`border text-gray-700 ${compactTables ? "px-2 py-1" : "px-3 py-2"}`}>
                        {getStageText(item)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
